import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { PaidMediaOverviewAttributionConfidenceData } from "src/services/PaidMediaOverviewAPI";
import { formatOverviewPct } from "../../utils/paidMediaOverviewFormat";
import {
  OverviewChartLoadingState,
  OverviewMetricTip,
  OverviewShareBar,
  type OverviewShareBarVariant,
} from "./overviewPrimitives";

interface AttributionConfidenceCardProps {
  data: PaidMediaOverviewAttributionConfidenceData | null;
  loading: boolean;
  storeUnavailable: boolean;
}

const CONFIDENCE_COMPONENTS: Array<{
  key: keyof PaidMediaOverviewAttributionConfidenceData["components"];
  labelKey: string;
  tooltipId: string;
  variant: OverviewShareBarVariant;
}> = [
  { key: "recognized_source", labelKey: "componentRecognizedSource", tooltipId: "recognizedSource", variant: "confidence-green" },
  { key: "campaign_id", labelKey: "componentCampaignId", tooltipId: "campaignIdSignal", variant: "confidence-blue" },
  { key: "ad_id", labelKey: "componentAdId", tooltipId: "adIdSignal", variant: "confidence-purple" },
  { key: "usable_utm", labelKey: "componentUsableUtm", tooltipId: "usableUtmSignal", variant: "confidence-orange" },
];

const AttributionConfidenceCard: FC<AttributionConfidenceCardProps> = ({
  data,
  loading,
  storeUnavailable,
}) => {
  const { t } = useTranslation();

  const renderBody = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (storeUnavailable) {
      return (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attributionConfidence.storeUnavailable")}
        </p>
      );
    }
    if (!data || data.eligible_paid_media_orders === 0 || data.attribution_confidence === null) {
      return (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attributionConfidence.noEligibleOrders")}
        </p>
      );
    }

    return (
      <>
        <div className="paid-media-overview__confidence-score">
          <div className="paid-media-overview__confidence-score-value">
            {formatOverviewPct(data.attribution_confidence, 0)}
          </div>
          <div className="paid-media-overview__confidence-score-label">
            {data.confidence_label ?? t("paidMediaOverview.attributionConfidence.confidenceLabelFallback")}
          </div>
        </div>
        <div className="paid-media-overview__confidence-components">
          {CONFIDENCE_COMPONENTS.map(({ key, labelKey, tooltipId, variant }) => {
            const component = data.components?.[key];
            return (
              <OverviewShareBar
                key={key}
                label={t(`paidMediaOverview.attributionConfidence.${labelKey}`)}
                value={component?.percentage ?? null}
                variant={variant}
                tooltipId={tooltipId}
                right={formatOverviewPct(component?.percentage ?? null, 0)}
              />
            );
          })}
        </div>
        <p className="paid-media-overview__confidence-footnote">
          {t("paidMediaOverview.attributionConfidence.description")}
        </p>
      </>
    );
  };

  return (
    <article className="paid-media-panel paid-media-overview__attribution-chart-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.attributionConfidence.title")}
            <OverviewMetricTip
              id="attributionConfidenceScore"
              label={t("paidMediaOverview.attributionConfidence.title")}
            />
          </h3>
        </div>
      </div>
      {renderBody()}
    </article>
  );
};

export default AttributionConfidenceCard;
